import {
  StrictLocalizeFunction,
  useStrictLocalization,
} from "@faire/web/common/localization";
import { Row, Column } from "@faire/web/slate/Layout";
import {
  getSpacing,
  Spacer,
  SpacerMultipliersKey,
} from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { FAIRE_ACTIONS } from "@faire/retailer/views/careers/__internal__/BelongingAtFaire/faireActions";
import {
  DefinitionTerm,
  EyebrowText,
  LimitedSectionContent,
  Section,
  SectionHeadline,
} from "@faire/retailer/views/careers/__internal__/common/common";

const BelongingWordsInActionBase = () => {
  const headlineSpace = useResponsiveValue<SpacerMultipliersKey>({
    mobileAndAbove: "7x",
    tabletAndAbove: "8x",
    desktopAndAbove: "10x",
  });
  const { isDesktopAndAbove, isTabletAndBelow } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  return (
    <Section $withHorizontalPadding $withVerticalPadding align="center">
      <LimitedSectionContent>
        <EyebrowText>
          <StrictLocalMsg
            defaultMessage="Our Words In Action"
            description={{
              text: "The heading for a section listing the company's actions on building a culture of belonging",
            }}
          />
        </EyebrowText>
        <Spacer height="3x" />
        <StyledHeadline>
          <StrictLocalMsg
            defaultMessage="How we’re building a culture of belonging"
            description={{
              text: "The head line for a section listing the company's actions on building a culture of belonging",
            }}
          />
        </StyledHeadline>
        <Spacer height={headlineSpace} />
        <Column align={isDesktopAndAbove ? "flex-end" : "flex-start"}>
          {isTabletAndBelow
            ? renderSingleColumn(strictLocalize)
            : renderDoubleColumn(strictLocalize)}
        </Column>
      </LimitedSectionContent>
    </Section>
  );
};

export const BelongingWordsInAction = observer(BelongingWordsInActionBase);

const renderDoubleColumn = (strictLocalize: StrictLocalizeFunction) => {
  const columnHeight = Math.ceil(FAIRE_ACTIONS.length / 2);

  return (
    <Grid $rows={columnHeight}>
      {FAIRE_ACTIONS(strictLocalize).map((action) => {
        return (
          <React.Fragment key={action.title}>
            <CellComponent
              title={action.title}
              description={action.description}
              number={action.number}
            />
          </React.Fragment>
        );
      })}
    </Grid>
  );
};

const renderSingleColumn = (strictLocalize: StrictLocalizeFunction) => {
  return (
    <React.Fragment>
      {FAIRE_ACTIONS(strictLocalize).map((action, index) => {
        return (
          <React.Fragment key={action.title}>
            {index === 0 ? null : <Spacer size="5x" />}
            <CellComponent
              title={action.title}
              description={action.description}
              number={action.number}
            />
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
};

interface CellProps {
  title: string;
  description: string;
  number?: string;
}

const CellComponent = ({ title, description, number }: CellProps) => {
  const { isTabletAndAbove } = useViewport();
  return (
    <dl style={{ height: "fit-content" }}>
      <Row align="flex-start">
        {isTabletAndAbove && number ? (
          <>
            <DefinitionTerm>{number}</DefinitionTerm>
            <Spacer size="2x" />
          </>
        ) : null}
        <Column>
          <DefinitionTerm>{title}</DefinitionTerm>
          <Spacer size="1x" />
          <MaxWidthTypography>{description}</MaxWidthTypography>
        </Column>
      </Row>
    </dl>
  );
};

const StyledHeadline = styled(SectionHeadline)`
  max-width: ${getSpacing(450)};

  ${responsiveMediaQueries.tabletAndBelow} {
    max-width: ${getSpacing(346)};
  }
`;

const Grid = styled.div<{ $rows: number }>`
  display: grid;
  column-gap: ${getSpacing("7x")};
  grid-template-rows: repeat(${(props) => props.$rows}, 1fr);
  grid-auto-flow: column;
`;

const MaxWidthTypography = styled(Typography)`
  max-width: ${getSpacing(368)};
  ${responsiveMediaQueries.tabletAndBelow} {
    max-width: ${getSpacing(470)};
  }
`;
